import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import type {
  CampaignCreatePayload,
  CampaignInterface,
  CampaignLaunchPayload,
  CampaignUpdatePayload
} from '@/interfaces/Campaign.interface';
import { useApiClient } from '../context';

/**
 * On success, invalidate `campaignsQueryKeys.lists()` and optionally
 * `campaignsQueryKeys.detail(uuid)` for the created campaign.
 */
export function useCampaignCreateMutation(
  options?: UseMutationOptions<
    CampaignInterface,
    Error,
    CampaignCreatePayload
  >
) {
  const api = useApiClient();
  return useMutation({
    ...options,
    mutationFn: (payload: CampaignCreatePayload) =>
      api.campaigns.create(payload)
  });
}

export type CampaignUpdateVariables = {
  uuid: string;
  payload: CampaignUpdatePayload;
};

/**
 * On success, invalidate `campaignsQueryKeys.detail(uuid)` and
 * `campaignsQueryKeys.lists()`.
 */
export function useCampaignUpdateMutation(
  options?: UseMutationOptions<
    CampaignInterface,
    Error,
    CampaignUpdateVariables
  >
) {
  const api = useApiClient();
  return useMutation({
    ...options,
    mutationFn: ({ uuid, payload }) => api.campaigns.update(uuid, payload)
  });
}

export type CampaignDeleteVariables = {
  uuid: string;
};

export type CampaignDuplicateVariables = {
  uuid: string;
};

export type CampaignLaunchVariables = {
  uuid: string;
  payload: CampaignLaunchPayload;
};

export type CampaignPauseVariables = {
  uuid: string;
};

export type CampaignResumeVariables = {
  uuid: string;
};

/**
 * Pauses a campaign and demotes its workflows via CRM API.
 */
export function useCampaignPauseMutation(
  options?: UseMutationOptions<CampaignInterface, Error, CampaignPauseVariables>
) {
  const api = useApiClient();
  return useMutation({
    ...options,
    mutationFn: ({ uuid }) => api.campaigns.pause(uuid)
  });
}

/**
 * Resumes a paused campaign and re-publishes workflows via CRM API.
 */
export function useCampaignResumeMutation(
  options?: UseMutationOptions<CampaignInterface, Error, CampaignResumeVariables>
) {
  const api = useApiClient();
  return useMutation({
    ...options,
    mutationFn: ({ uuid }) => api.campaigns.resume(uuid)
  });
}

/**
 * Promotes campaign workflows and marks the campaign launched via CRM API.
 */
export function useCampaignLaunchMutation(
  options?: UseMutationOptions<CampaignInterface, Error, CampaignLaunchVariables>
) {
  const api = useApiClient();
  return useMutation({
    ...options,
    mutationFn: ({ uuid, payload }) => api.campaigns.launch(uuid, payload)
  });
}

/**
 * On success, invalidate `campaignsQueryKeys.lists()`.
 */
export function useCampaignDeleteMutation(
  options?: UseMutationOptions<void, Error, CampaignDeleteVariables>
) {
  const api = useApiClient();
  return useMutation({
    ...options,
    mutationFn: ({ uuid }) => api.campaigns.delete(uuid)
  });
}

/**
 * On success, invalidate `campaignsQueryKeys.lists()`.
 */
export function useCampaignDuplicateMutation(
  options?: UseMutationOptions<
    CampaignInterface,
    Error,
    CampaignDuplicateVariables
  >
) {
  const api = useApiClient();
  return useMutation({
    ...options,
    mutationFn: ({ uuid }) => api.campaigns.duplicate(uuid)
  });
}
